/**
 * PILGRIM Load More
 *
 * Progressive enhancement for the collection grid. The forked
 * `pilgrim-main-collection` section renders Horizon's native numbered pager (the
 * no-JS fallback / SEO crawl path) plus a hidden <pilgrim-load-more> element.
 * This controller upgrades that element into a "Load more" button with an
 * ASOS-style "Showing X of Y" status + progress bar.
 *
 * It reuses Horizon's Section Rendering API via the same `sectionRenderer`
 * singleton the dormant <results-list> prefetches with — so the first click is
 * usually a cache hit — and the grid's existing `[ref="grid"]` /
 * `[ref="cards[]"]` / `data-last-page` contract, so the PILGRIM toolbar's
 * filter/sort wiring keeps working untouched.
 *
 * Back-button restoration:
 *   - On production the browser back/forward cache restores the whole page
 *     (every loaded batch + scroll position) instantly, and this controller
 *     doesn't even re-run.
 *   - When bfcache misses (the `theme dev` proxy, memory pressure, older
 *     browsers) the back navigation reloads page 1 only, so we restore
 *     deterministically: the loaded batch count + scroll offset are saved on
 *     `pagehide`, and on a back/forward (or reload) navigation we re-fetch the
 *     batches in parallel, append them, then restore the scroll position.
 *   - We take over scroll restoration (`history.scrollRestoration = 'manual'`)
 *     so the browser can't restore the old offset against the shorter
 *     page-1-only DOM (which would land the shopper near the button).
 */

import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';

const SELECTOR_GRID = '[ref="grid"]';
const SELECTOR_CARDS = '[ref="cards[]"]';
// Products only. Collection spots also carry ref="cards[]" (so they append on Load
// More and restore on back/forward), but must not count toward "Showing X of Y".
const SELECTOR_PRODUCTS = '[ref="cards[]"]:not([data-pilgrim-spot])';
const SELECTOR_PAGER = '.pagination';
const REINIT_FALLBACK_MS = 1500;
const SCROLL_RESTORE_BUDGET_MS = 4000;
const STORAGE_PREFIX = 'pilgrim-load-more:';

class PilgrimLoadMore extends HTMLElement {
  /** @type {HTMLElement | null} */
  #grid = null;

  /** @type {HTMLElement | null} */
  #pager = null;

  /** @type {HTMLButtonElement | null} */
  #button = null;

  /** @type {HTMLElement | null} */
  #status = null;

  /** @type {HTMLElement | null} */
  #progress = null;

  /** @type {number} */
  #nextPage = 2;

  /** @type {number} */
  #lastPage = 1;

  /** @type {number} */
  #total = 0;

  /** @type {boolean} */
  #loading = false;

  /** @type {MutationObserver | undefined} */
  #reinitObserver;

  connectedCallback() {
    this.#button = this.querySelector('.pilgrim-load-more__button');
    this.#status = this.querySelector('.pilgrim-load-more__status');
    this.#progress = this.querySelector('.pilgrim-load-more__progress');

    // Own scroll restoration so the browser doesn't restore a stale offset
    // against the (shorter) page-1-only DOM before we rebuild it.
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

    this.#button?.addEventListener('click', this.#handleClick);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    window.addEventListener('pagehide', this.#saveState);

    this.#init();
    this.#restoreScroll();
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#handleClick);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    window.removeEventListener('pagehide', this.#saveState);
    this.#reinitObserver?.disconnect();
  }

  get #sectionId() {
    return this.dataset.pilgrimSectionId ?? '';
  }

  /**
   * Cache refs and sync the UI to the current DOM state. Idempotent, so it runs
   * on first connect and again after every filter/sort re-render.
   */
  #init() {
    const resultsList = this.closest('results-list');
    this.#grid = resultsList?.querySelector(SELECTOR_GRID) ?? null;
    this.#pager = resultsList?.querySelector(SELECTOR_PAGER) ?? null;

    if (!this.#grid) return;

    this.#total = Number(this.dataset.pilgrimTotal) || this.#countCards();
    this.#lastPage = Number(this.#grid.dataset.lastPage) || 1;
    this.#nextPage = this.#highestLoadedPage() + 1;

    // Keep the numbered pager in the DOM (no-JS / crawlers) but hide it now that
    // the button is taking over.
    if (this.#pager) this.#pager.hidden = true;

    this.#syncUI();
  }

  #countCards() {
    return this.#grid ? this.#grid.querySelectorAll(SELECTOR_PRODUCTS).length : 0;
  }

  #highestLoadedPage() {
    if (!this.#grid) return 1;

    let highest = 1;
    for (const card of this.#grid.querySelectorAll(SELECTOR_CARDS)) {
      const page = Number(card.getAttribute('data-page'));
      if (page > highest) highest = page;
    }
    return highest;
  }

  #handleClick = () => {
    this.#loadMore();
  };

  /**
   * Fetch one page of product cards through Horizon's Section Rendering API.
   * Shared by Load more and back-button restoration.
   * @param {number} pageNumber
   * @returns {Promise<Element[]>}
   */
  async #fetchPageCards(pageNumber) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(pageNumber));
    url.hash = '';
    const useCache = !(window.Shopify && window.Shopify.designMode);
    const html = await sectionRenderer.getSectionHTML(this.#sectionId, useCache, url);
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return Array.from(doc.querySelectorAll(`${SELECTOR_GRID} > ${SELECTOR_CARDS}`));
  }

  async #loadMore() {
    if (this.#loading || !this.#grid || this.#nextPage > this.#lastPage) return;
    this.#setLoading(true);

    try {
      const newCards = await this.#fetchPageCards(this.#nextPage);

      if (newCards.length === 0) {
        // Nothing came back — treat this as the end of the list.
        this.#lastPage = this.#nextPage - 1;
        this.#syncUI();
        return;
      }

      this.#grid.append(...newCards);
      this.#nextPage += 1;
      this.#syncUI();

      // When the list is exhausted the button is gone, so move focus to the live
      // status to keep keyboard users oriented. Otherwise focus stays on the
      // button for repeat clicks; the aria-live status announces the new count.
      if (this.#nextPage > this.#lastPage) {
        this.#status?.focus({ preventScroll: true });
      }
    } catch {
      this.#showError();
    } finally {
      this.#setLoading(false);
    }
  }

  #syncUI() {
    const shown = this.#countCards();
    const total = Math.max(this.#total, shown);
    const atEnd = this.#nextPage > this.#lastPage;

    if (this.#status) {
      const template = atEnd
        ? this.dataset.pilgrimAllLoadedTemplate ?? ''
        : this.dataset.pilgrimStatusTemplate ?? '';
      this.#status.textContent = this.#interpolate(template, {
        shown: this.#formatNumber(shown),
        total: this.#formatNumber(total),
      });
    }

    if (this.#progress) {
      const pct = total > 0 ? Math.min(100, Math.round((shown / total) * 100)) : 0;
      this.#progress.style.setProperty('--pilgrim-load-more-pct', `${pct}%`);
    }

    if (this.#button) this.#button.hidden = atEnd;
  }

  #setLoading(loading) {
    this.#loading = loading;
    if (!this.#button) return;
    this.#button.disabled = loading;
    this.#button.setAttribute('aria-busy', String(loading));
  }

  #showError() {
    // Leave the button enabled (the `finally` clears loading) so the shopper can
    // retry, and announce the failure through the live status region.
    if (this.#status && this.dataset.pilgrimError) {
      this.#status.textContent = this.dataset.pilgrimError;
    }
  }

  // --- Back-button restoration -------------------------------------------------

  /** Storage key for the current collection + filters (page/hash excluded). */
  #storageKey() {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.hash = '';
    url.searchParams.sort();
    return `${STORAGE_PREFIX}${url.pathname}${url.search}`;
  }

  #saveState = () => {
    if (!this.#grid) return;
    try {
      sessionStorage.setItem(
        this.#storageKey(),
        JSON.stringify({ pages: this.#highestLoadedPage(), y: Math.round(window.scrollY) })
      );
    } catch {
      // sessionStorage can be unavailable (private mode / quota); restore is best-effort.
    }
  };

  /**
   * On a back/forward (or reload) navigation, rebuild the batches the shopper had
   * loaded and restore their scroll position. Skipped on fresh navigations and
   * when the browser bfcache already restored the page (this controller doesn't
   * re-run in that case).
   */
  async #restoreScroll() {
    if (!this.#grid) return;

    const navigation = performance.getEntriesByType('navigation')[0];
    const navType = navigation?.type;
    if (navType !== 'back_forward' && navType !== 'reload') return;

    let saved = null;
    try {
      const raw = sessionStorage.getItem(this.#storageKey());
      if (raw) saved = JSON.parse(raw);
    } catch {
      saved = null;
    }
    if (!saved) return;

    const targetPage = Math.min(Number(saved.pages) || 1, this.#lastPage);
    if (targetPage > this.#highestLoadedPage()) {
      await this.#restoreTo(targetPage);
    }

    this.#restoreScrollPosition(Number(saved.y) || 0);
  }

  /**
   * Restore the saved scroll offset. A back/forward navigation starts at the top
   * (scrollRestoration='manual') and the page is still settling — the appended
   * cards' heights and render (briefly held by the view-transition blocking link)
   * aren't ready, so an early scroll would clamp. We poll on animation frames
   * until the page is actually tall enough to reach the target, then scroll, and
   * keep re-asserting through any load-time snap-back to the top — yielding the
   * moment the shopper scrolls away. Bounded to ~3s.
   * @param {number} targetY
   */
  #restoreScrollPosition(targetY) {
    if (targetY <= 0) return;

    const start = performance.now();
    const apply = () => window.scrollTo({ top: targetY, behavior: 'instant' });

    const step = () => {
      const elapsed = performance.now() - start;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const reachable = maxScroll >= targetY - 2;

      if (reachable) apply();

      const settled = Math.abs(window.scrollY - targetY) <= 2;
      // Poll on a timer rather than requestAnimationFrame: the view-transition
      // `blocking="render"` link defers rAF until paint unblocks (seconds, on a
      // slow back navigation), but the scroll offset is settable before then.
      // Keep re-asserting while the page still can't reach the target (layout
      // settling) or it snapped back to the top (a load-time reset); stop once
      // settled, if the shopper scrolls away, or after the budget.
      if (!settled && elapsed < SCROLL_RESTORE_BUDGET_MS && (!reachable || window.scrollY < 50)) {
        setTimeout(step, 50);
      }
    };

    step();
  }

  /**
   * Re-fetch and append pages from the current end up to targetPage, in parallel
   * (wall-clock ≈ one request, not N), preserving page order.
   * @param {number} targetPage
   */
  async #restoreTo(targetPage) {
    if (!this.#grid) return;

    const pageNumbers = [];
    for (let page = this.#nextPage; page <= targetPage && page <= this.#lastPage; page += 1) {
      pageNumbers.push(page);
    }
    if (pageNumbers.length === 0) return;

    const batches = await Promise.all(
      pageNumbers.map((page) => this.#fetchPageCards(page).catch(() => null))
    );
    for (const cards of batches) {
      if (cards && cards.length) this.#grid.append(...cards);
    }
    this.#nextPage = Math.min(targetPage, this.#lastPage) + 1;
    this.#syncUI();
  }

  // --- Filter/sort re-render ---------------------------------------------------

  #handleFilterUpdate = () => {
    // The toolbar re-renders the grid section on filter/sort. Wait for that morph
    // to settle, then re-sync (reset the page counter, re-read the new total,
    // re-hide the fresh pager).
    this.#reinitObserver?.disconnect();

    const resultsList = this.closest('results-list');
    if (!resultsList) {
      this.#init();
      return;
    }

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      this.#reinitObserver?.disconnect();
      this.#init();
    };

    this.#reinitObserver = new MutationObserver(() => requestAnimationFrame(finish));
    this.#reinitObserver.observe(resultsList, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-last-page'],
    });

    // Fallback for the rare case where the filtered result is identical and
    // nothing in the grid mutates.
    setTimeout(finish, REINIT_FALLBACK_MS);
  };

  /**
   * Replace [token] placeholders with values. Pairs with the Liquid-rendered
   * templates that carry localized copy plus [shown] / [total] markers.
   * @param {string} template
   * @param {Record<string, string>} values
   */
  #interpolate(template, values) {
    return template.replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));
  }

  /** @param {number} value */
  #formatNumber(value) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined).format(value);
    } catch {
      return String(value);
    }
  }
}

if (!customElements.get('pilgrim-load-more')) {
  customElements.define('pilgrim-load-more', PilgrimLoadMore);
}
